import React from "react";
import { EncounterConditionDisplay, EncounterConditionDisplayModified, TenantProperty, User } from "../../authenticated/types";;
import { booleanPropertyEnabled } from "../../common/tenantPropertyUtilsTwo";
import { TENANT_PROPERTY } from '../../../constant'
import ConditionReasons from "./conditionReasons";
import { encounterConditionClinicalAdminDisplayVersion } from '../../common/currentUserUtils'
import { EvidenceViewer } from "../../mdp";

type Props = {
  condition: EncounterConditionDisplay | EncounterConditionDisplayModified,
  currentUser: User
};

const ConditionEvidence = (props: Props): JSX.Element => {
  const { currentUser, condition } = props;
  const conditionRuleType = condition.rules && condition.rules[0] && condition.rules[0].targetType;
  const clinicalEvidenceArray = JSON.parse(JSON.stringify(condition.clinicalEvidence))
  const clinicalEvidenceMap = new Map<String, Object>();
  for(const ce of clinicalEvidenceArray || []){
      const existing = clinicalEvidenceMap.get(ce.content)
      if(existing === undefined || ce.priority < existing['priority' as keyof typeof existing]){
        clinicalEvidenceMap.set(ce.content, ce)
      }
  }
  const filteredClinicalEvidence = Array.from(clinicalEvidenceMap.values())

  return (
    <div key={condition.id}>
      {displayReason(currentUser) && (
        <ConditionReasons condition={condition} />
      )}
      {displayClinicalEvidence(condition, conditionRuleType, currentUser.tenantProperties) && (
        <div id={`evidence-${condition.id}`}
          key={`evidence-${condition.id}`}
          style={{ paddingLeft: '2em', marginBottom: '1em'}}>
          {`Clinical Evidence:`}
          {filteredClinicalEvidence
            .sort((a: any, b: any) => {
              const priorityDiff = a.priority - b.priority;
              if (priorityDiff !== 0) return priorityDiff;
              return a.content.localeCompare(b.content);
            })
            .slice(0, 3)
            .map((ce: any) =>
                <div
                  key={`clinical-evidence-content-${ce.sourceId}`}
                  id={`clinical-evidence-content-${ce.sourceId}`}
                  style={{ marginTop: '1em' }}
                >
                  <ul>
                    <li>
                      {ce.content}
                      {showEvidenceViewer(ce) && (
                        <span style={{ marginLeft: '0.5em' }}>
                          <EvidenceViewer nlpSourceType={ce.sourceType} nlpSourceId={ce.sourceId} />
                        </span>
                      )}
                    </li>
                  </ul>
                </div>
            )}
        </div>
      )}
    </div>
  )
}

  const displayClinicalEvidence = (
    condition: EncounterConditionDisplay | EncounterConditionDisplayModified,
    ruleType: string | null,
    tenantProps: TenantProperty[]
  ) => {
    if (!condition.clinicalEvidence || !ruleType) {
      return false;
    }

    return handleCmsV28(condition, ruleType, tenantProps) ||
           handleHhsV07(condition, ruleType, tenantProps) ||
           handleHhsV08(condition, ruleType, tenantProps) ||
           handleMcdY22(condition, ruleType, tenantProps)
  }

  const handleCmsV28 = (
    condition: EncounterConditionDisplay | EncounterConditionDisplayModified,
    ruleType: string | null,
    tenantProps: TenantProperty[]
  ) => {
    if (!isCmsV28(condition)) {
      return false
    }
    const cmsV28PdrDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_CMSV28_PDR_EXCLUDE_CLINICAL_EVIDENCE)

    const cmsV28OvlDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_CMSV28_OVL_EXCLUDE_CLINICAL_EVIDENCE)

    const cmsV28MedDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_CMSV28_MED_EXCLUDE_CLINICAL_EVIDENCE)

    return (
      (!cmsV28PdrDisabled && ruleType === 'DEVICE') ||
      (!cmsV28OvlDisabled && ruleType === 'OBSERVATION') ||
      (!cmsV28MedDisabled && ruleType === 'MEDICATION') ||
      (ruleType === 'NLP_INTERNAL')
    )
  }

  const handleHhsV07 = (
    condition: EncounterConditionDisplay | EncounterConditionDisplayModified,
    ruleType: string | null,
    tenantProps: TenantProperty[]
  ) => {
    if (!isHhsV07(condition)) {
      return false
    }

    const hhsV07PdrDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_HHSV07_PDR_EXCLUDE_CLINICAL_EVIDENCE)

    const hhsV07OvlDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_HHSV07_OVL_EXCLUDE_CLINICAL_EVIDENCE)

    const hhsV07MedDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_HHSV07_MED_EXCLUDE_CLINICAL_EVIDENCE)

    const hhsV07CmbDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_HHSV07_CMB_EXCLUDE_CLINICAL_EVIDENCE)

    return (!hhsV07OvlDisabled && ruleType === 'OBSERVATION') ||
           (!hhsV07MedDisabled && ruleType === 'MEDICATION') ||
           (!hhsV07PdrDisabled && ruleType === 'DEVICE') ||
           (!hhsV07CmbDisabled && ruleType === 'COMBO')
  }

  const handleHhsV08 = (
    condition: EncounterConditionDisplay | EncounterConditionDisplayModified,
    ruleType: string | null,
    tenantProps: TenantProperty[]
  ) => {
    if (!isHhsV08(condition)) {
      return false
    }

    const hhsV08PdrDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_HHSV08_PDR_EXCLUDE_CLINICAL_EVIDENCE)

    const hhsV08OvlDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_HHSV08_OVL_EXCLUDE_CLINICAL_EVIDENCE)

    const hhsV08MedDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_HHSV08_MED_EXCLUDE_CLINICAL_EVIDENCE)

    const hhsV08CmbDisabled = booleanPropertyEnabled(
      tenantProps, TENANT_PROPERTY.RULE_HHSV08_CMB_EXCLUDE_CLINICAL_EVIDENCE)

    return (!hhsV08OvlDisabled && ruleType === 'OBSERVATION') ||
           (!hhsV08MedDisabled && ruleType === 'MEDICATION') ||
           (!hhsV08PdrDisabled && ruleType === 'DEVICE') ||
           (!hhsV08CmbDisabled && ruleType === 'COMBO')
  }

  const handleMcdY22 = (
    condition: EncounterConditionDisplay | EncounterConditionDisplayModified,
    ruleType: string | null,
    tenantProps: TenantProperty[]
  ) => {
    return false
    // if (!isMcdY22(condition)) {
    //   return false
    // }
    // const mcdY22OvlDisabled = booleanPropertyEnabled(
    //    tenantProps, TENANT_PROPERTY.RULE_MCDY22_OVL_EXCLUDE_CLINICAL_EVIDENCE)
    // const mcdY22MedDisabled = booleanPropertyEnabled(
    //   tenantProps, TENANT_PROPERTY.RULE_MCDY22_MED_EXCLUDE_CLINICAL_EVIDENCE)
    // future work goes here
  }

  const isCmsV28 = (condition: EncounterConditionDisplay | EncounterConditionDisplayModified): boolean => {
    return !!condition.hccVersion && 'CMS_V28' === condition.hccVersion
  }

  const isHhsV07 = (condition: EncounterConditionDisplay | EncounterConditionDisplayModified): boolean => {
    return !!condition.hccVersion && 'HHS_V07' === condition.hccVersion
  }

  const isHhsV08 = (condition: EncounterConditionDisplay | EncounterConditionDisplayModified): boolean => {
    return !!condition.hccVersion && 'HHS_V08' === condition.hccVersion
  }

  const isMcdY22 = (condition: EncounterConditionDisplay | EncounterConditionDisplayModified): boolean => {
    return !!condition.hccVersion && 'MCD_Y22' === condition.hccVersion
  }

  const displayReason = (currentUser: User) => {
    if (currentUser.type === 'CLINICAL_ADMIN') {
      return encounterConditionClinicalAdminDisplayVersion(currentUser) != '2'
    }
    return currentUser.type != 'PHYSICIAN'
  }

  const showEvidenceViewer = (clinicalEvidence: any) => clinicalEvidence !== null && clinicalEvidence.sourceId !== null && ['pdf', 'xml'].includes(clinicalEvidence.sourceType)


export default ConditionEvidence;